import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../../supabase';
import Dropdown from '../../../../components/Dropdown';
import SearchableProjectDropdown from '../../../../components/SearchableProjectDropdown';
import { DropdownItem } from '../../../../components/Dropdown/config';
import {
  INVOICE_STATUS_OPTIONS,
  calculateGST,
  calculateTotal,
  formatCurrency,
  formatDate,
  Invoice,
  shouldMirrorRecordedDate
} from '../../config';
import Modal from '../../../../components/Modal';
import './styles.scss';

// =======================================
// COPY BUTTON COMPONENT
// =======================================

interface CopyButtonProps {
  value: string;
  title?: string;
}

const CopyButton: React.FC<CopyButtonProps> = ({ value, title = 'Copy to clipboard' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [value]);

  return (
    <button
      type="button"
      className={`copy-btn ${copied ? 'copy-btn--copied' : ''}`}
      onClick={handleCopy}
      title={copied ? 'Copied!' : title}
    >
      {copied ? (
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
        </svg>
      )}
    </button>
  );
};

// =======================================
// INTERFACES
// =======================================

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

interface InvoiceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onInvoiceUpdated: () => void;
  onInvoiceDeleted: () => void;
  teamMembers: TeamMember[];
}

interface FormData {
  invoice_number: string;
  invoice_date: string;
  recorded_date: string;
  project_id: number | '';
  stage_id: number | null;
  our_fee: string;
  disbursements: string;
  status: string;
  notes: string;
}

interface FormErrors {
  invoice_number?: string;
  invoice_date?: string;
  project_id?: string;
  our_fee?: string;
  general?: string;
}


const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  isOpen,
  onClose,
  invoice,
  onInvoiceUpdated,
  onInvoiceDeleted,
  teamMembers
}) => {
  const navigate = useNavigate();
  const isBundled = !!invoice?.bundled_in_invoice_id;
  // Lock the modal when status isn't Draft. Status itself stays editable so
  // the user can revert to Draft.
  const isStatusLocked = !!invoice && invoice.status !== 'Draft';
  const isFieldLocked = isBundled || isStatusLocked;
  // When line items exist, our_fee + disbursements are owned by the tax
  // invoice editor (which sends raw values). The list row carries
  // period-allocated values for display only — submitting them back would
  // overwrite the raw with the period-allocated figure (catastrophic for
  // parents with bundled children: raw would be silently halved-ish).
  // Lock these two fields read-only when this row already has line items.
  const isFeeManagedByEditor = !!invoice && (invoice.line_item_count ?? 0) > 0 && !isBundled;
  const [formData, setFormData] = useState<FormData>({
    invoice_number: '',
    invoice_date: '',
    recorded_date: '',
    project_id: '',
    stage_id: null,
    our_fee: '',
    disbursements: '',
    status: '',
    notes: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  // Parent invoice number for bundled rows. The register row only carries
  // the FK id, so we look up the parent's number on open to render a more
  // specific banner ("Bundled into TE12345" rather than "another invoice").
  const [parentInvoiceNumber, setParentInvoiceNumber] = useState<string | null>(null);
  const [originalInvoiceNumber, setOriginalInvoiceNumber] = useState<string>('');
  const [selectedClientName, setSelectedClientName] = useState<string | null>(null);
  const duplicateCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate totals
  const ourFee = parseFloat(formData.our_fee) || 0;
  const disbursements = parseFloat(formData.disbursements) || 0;
  const gstAmount = calculateGST(ourFee, disbursements);
  const totalAmount = calculateTotal(ourFee, disbursements);

  // Check if invoice number already exists (excluding current invoice)
  const checkDuplicateInvoiceNumber = useCallback(async (invoiceNumber: string) => {
    if (!invoiceNumber.trim() || !invoice) {
      setIsDuplicate(false);
      return;
    }

    const fullInvoiceNumber = `TE${invoiceNumber.trim()}`;
    
    // Don't check if it's the same as original
    if (fullInvoiceNumber === originalInvoiceNumber) {
      setIsDuplicate(false);
      setErrors(prev => ({ ...prev, invoice_number: undefined }));
      return;
    }

    setIsCheckingDuplicate(true);
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('id')
        .eq('invoice_number', fullInvoiceNumber)
        .neq('id', invoice.id)
        .limit(1);

      if (error) throw error;

      const exists = data && data.length > 0;
      setIsDuplicate(exists);
      if (exists) {
        setErrors(prev => ({ ...prev, invoice_number: 'Invoice number already exists' }));
      } else {
        setErrors(prev => ({ ...prev, invoice_number: undefined }));
      }
    } catch (err) {
      console.error('Error checking duplicate:', err);
    } finally {
      setIsCheckingDuplicate(false);
    }
  }, [invoice, originalInvoiceNumber]);

  // Fetch client name for a project
  const fetchClientName = useCallback(async (projectId: number) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('clients!client(company_name)')
        .eq('id', projectId)
        .single();

      if (error) throw error;
      setSelectedClientName((data?.clients as any)?.company_name || null);
    } catch (err) {
      console.error('Error fetching client:', err);
      setSelectedClientName(null);
    }
  }, []);

  // Initialize form data when invoice changes
  useEffect(() => {
    if (invoice && isOpen) {
      // Extract number from invoice_number (remove TE prefix)
      const invoiceNum = invoice.invoice_number?.replace(/^TE/, '') || '';
      
      setFormData({
        invoice_number: invoiceNum,
        invoice_date: invoice.invoice_date,
        recorded_date: invoice.recorded_date || invoice.invoice_date,
        project_id: invoice.project_id !== null && invoice.project_id !== undefined ? invoice.project_id : '',
        stage_id: invoice.stage_id || null,
        our_fee: invoice.our_fee ? invoice.our_fee.toString() : '0',
        disbursements: invoice.disbursements ? invoice.disbursements.toString() : '0',
        status: invoice.status || 'Draft',
        notes: invoice.notes || ''
      });
      setOriginalInvoiceNumber(invoice.invoice_number || '');
      setErrors({});
      setShowDeleteConfirm(false);
      setIsDuplicate(false);
      setIsCheckingDuplicate(false);
      
      // Fetch client name for existing project
      if (invoice.project_id) {
        fetchClientName(invoice.project_id);
      } else {
        setSelectedClientName(null);
      }
    }

    return () => {
      if (duplicateCheckTimeoutRef.current) {
        clearTimeout(duplicateCheckTimeoutRef.current);
      }
    };
  }, [invoice, isOpen, fetchClientName]);

  // Look up the parent's invoice_number for bundled rows so the banner
  // can name it. Bails out immediately for non-bundled invoices.
  useEffect(() => {
    if (!isOpen || !invoice?.bundled_in_invoice_id) {
      setParentInvoiceNumber(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('invoice_number')
        .eq('id', invoice.bundled_in_invoice_id)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setParentInvoiceNumber(null);
        return;
      }
      setParentInvoiceNumber(data.invoice_number);
    })();
    return () => { cancelled = true; };
  }, [isOpen, invoice?.bundled_in_invoice_id]);


  const validateForm = useCallback((): FormErrors => {
    const newErrors: FormErrors = {};

    // When the form is fully locked, the only thing we send is `status` — no
    // other field validation is relevant. (Crucially this also avoids
    // blocking a status revert on a bugged row whose period-allocated
    // `our_fee` shows as 0.)
    if (isFieldLocked) {
      return newErrors;
    }

    if (!formData.invoice_number.trim()) {
      newErrors.invoice_number = 'Invoice number is required';
    } else if (isDuplicate) {
      newErrors.invoice_number = 'Invoice number already exists';
    }

    if (!formData.invoice_date) {
      newErrors.invoice_date = 'Invoice date is required';
    }

    if (!formData.project_id) {
      newErrors.project_id = 'Project selection is required';
    }

    // Skip the our_fee check when the editor owns the value — the field
    // is read-only and not in the save payload, so its displayed value
    // (which may be the period-allocated figure or even 0 for legacy bugged
    // rows) shouldn't gate the form. A zero fee is allowed; only an empty or
    // negative value is rejected (empty would submit as NaN).
    if (!isFeeManagedByEditor && (!formData.our_fee || parseFloat(formData.our_fee) < 0)) {
      newErrors.our_fee = 'Our fee is required and cannot be negative';
    }

    return newErrors;
  }, [formData, isDuplicate, isFieldLocked, isFeeManagedByEditor]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!invoice) return;
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSaving(true);
    setErrors({});

    try {
      // When the invoice is locked (Sent/Entered or bundled), only the status
      // field is editable in the UI — and the list row's our_fee is the
      // *period-allocated* value (raw minus bundled children's), not the raw
      // DB value. Sending it back would silently overwrite the raw figure
      // (and the lock trigger would reject the write anyway).
      //
      // Even for editable Draft rows, `our_fee` / `disbursements` must NOT
      // be sent back when line items exist — the row's `our_fee` came back
      // as the period-allocated value, and writing it as the raw would
      // silently corrupt parents with bundled children.
      const baseUpdate = {
        invoice_number: `TE${formData.invoice_number.trim()}`,
        invoice_date: formData.invoice_date,
        recorded_date: formData.recorded_date || formData.invoice_date,
        project_id: typeof formData.project_id === 'number' ? formData.project_id : parseInt(formData.project_id),
        stage_id: formData.stage_id,
        status: formData.status,
        notes: formData.notes.trim() || null,
      };
      // recorded_date stays editable even when the invoice is locked (the edit
      // lock excludes it), so include it in the status-only locked payload too.
      const updateData = isFieldLocked
        ? { status: formData.status, recorded_date: formData.recorded_date || formData.invoice_date }
        : isFeeManagedByEditor
          ? baseUpdate
          : {
              ...baseUpdate,
              our_fee: parseFloat(formData.our_fee),
              disbursements: parseFloat(formData.disbursements) || 0,
            };

      const { error } = await supabase
        .from('invoices')
        .update(updateData)
        .eq('id', invoice.id);

      if (error) {
        throw error;
      }

      // Success - update parent
      onInvoiceUpdated();
      onClose();
    } catch (err) {
      console.error('Error updating invoice:', err);
      setErrors({
        general: err instanceof Error ? err.message : 'An error occurred while updating the invoice'
      });
    } finally {
      setIsSaving(false);
    }
  }, [formData, validateForm, invoice, onInvoiceUpdated, onClose, isFieldLocked, isFeeManagedByEditor]);

  const handleDelete = useCallback(async () => {
    if (!invoice) return;

    setIsDeleting(true);
    
    try {
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', invoice.id);

      if (error) {
        throw error;
      }

      // Success - notify parent
      onInvoiceDeleted();
      onClose();
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setErrors({
        general: err instanceof Error ? err.message : 'An error occurred while deleting the invoice'
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [invoice, onInvoiceDeleted, onClose]);

  const handleInputChange = useCallback((field: keyof FormData, value: string | number) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'invoice_date' && shouldMirrorRecordedDate(prev.recorded_date, prev.invoice_date)) {
        next.recorded_date = value as string;
      }
      return next;
    });
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Debounced duplicate check for invoice number
    if (field === 'invoice_number') {
      setIsDuplicate(false);
      if (duplicateCheckTimeoutRef.current) {
        clearTimeout(duplicateCheckTimeoutRef.current);
      }
      duplicateCheckTimeoutRef.current = setTimeout(() => {
        checkDuplicateInvoiceNumber(value as string);
      }, 500);
    }
  }, [errors, checkDuplicateInvoiceNumber]);

  // Handle project selection
  const handleProjectChange = useCallback((projectId: string | number | null, project?: any) => {
    if (projectId && project) {
      // Check if this is a stage or a project
      if (project.entity_type === 'stage') {
        // For stages: project_id is the parent, stage_id is the stage
        setFormData(prev => ({
          ...prev,
          project_id: project.parent_project_id || '',
          stage_id: typeof projectId === 'number' ? projectId : parseInt(projectId)
        }));
      } else {
        // For projects: just set project_id, stage_id is null
        setFormData(prev => ({
          ...prev,
          project_id: typeof projectId === 'number' ? projectId : parseInt(projectId),
          stage_id: null
        }));
      }
      // Set client name
      setSelectedClientName(project.client_company_name || null);
      // Clear error
      if (errors.project_id) {
        setErrors(prev => ({ ...prev, project_id: undefined }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        project_id: '',
        stage_id: null
      }));
      setSelectedClientName(null);
    }
  }, [errors]);

  // Create dropdown items
  const statusDropdownItems: DropdownItem[] = INVOICE_STATUS_OPTIONS.map(status => ({
    id: status.id,
    label: status.label,
    value: status.value
  }));

  if (!invoice) {
    return null;
  }

  const modalFooter = (
    <div className="modal-footer-actions invoice-footer">
      {!isFieldLocked && (
        <button
          type="button"
          className="btn-delete-icon"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isDeleting || isSaving}
          title="Delete Invoice"
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14M10 11v6M14 11v6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}
      <button
        type="button"
        className="btn-primary"
        onClick={() => {
          const form = document.getElementById('invoice-detail-form') as HTMLFormElement;
          if (form) {
            form.requestSubmit();
          }
        }}
        disabled={isSaving || isDeleting}
      >
        {isSaving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Invoice ${invoice.invoice_number}`}
        size="md"
        hasFooter={true}
        footerContent={modalFooter}
      >
        <div className="form-content invoice-edit-form">
          <form id="invoice-detail-form" onSubmit={handleSubmit}>
              {errors.general && (
                <div className="error-message general-error">
                  {errors.general}
                </div>
              )}

              {invoice.bundled_in_invoice_id && (
                <div className="bundled-banner">
                  <div className="bundled-banner__text">
                    <strong>
                      Bundled into {parentInvoiceNumber ? parentInvoiceNumber : 'another invoice'}.
                    </strong>{' '}
                    The tax invoice document and send action live there. Status follows the parent.
                  </div>
                  <button
                    type="button"
                    className="bundled-banner__btn"
                    onClick={() => {
                      onClose();
                      navigate(`/invoices/${invoice.bundled_in_invoice_id}`);
                    }}
                  >
                    {parentInvoiceNumber ? `Open ${parentInvoiceNumber}` : 'Open parent'}
                  </button>
                </div>
              )}

              {isStatusLocked && !isBundled && (
                <div className="bundled-banner">
                  <div className="bundled-banner__text">
                    <strong>Invoice is {invoice.status}.</strong>{' '}
                    Fields are locked. Revert <em>Status</em> to Draft to edit.
                  </div>
                </div>
              )}

              {/* Row 1: Invoice #, Sent and Recorded dates — packed to the left */}
              <div className="form-row form-row-header">
                <div className="form-group invoice-number-group">
                  <label htmlFor="edit_invoice_number">Invoice # *</label>
                  <div className="input-with-copy">
                    <div className={`invoice-number-input ${!isCheckingDuplicate && !isDuplicate && formData.invoice_number.trim() && `TE${formData.invoice_number}` !== originalInvoiceNumber ? 'valid' : ''}`}>
                      <span className="invoice-prefix">TE</span>
                      <input
                        id="edit_invoice_number"
                        type="text"
                        value={formData.invoice_number}
                        onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                        className={errors.invoice_number || isDuplicate ? 'error' : ''}
                        disabled={isFieldLocked}
                      />
                      {!isCheckingDuplicate && !isDuplicate && formData.invoice_number.trim() && `TE${formData.invoice_number}` !== originalInvoiceNumber && (
                        <span className="valid-indicator">✓</span>
                      )}
                    </div>
                    <CopyButton value={formData.invoice_number} title="Copy invoice number" />
                  </div>
                  {errors.invoice_number && <div className="error-message">{errors.invoice_number}</div>}
                </div>

                <div className="form-group date-group">
                  <label htmlFor="edit_invoice_date">Sent *</label>
                  <div className="input-with-copy">
                    <input
                      id="edit_invoice_date"
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => handleInputChange('invoice_date', e.target.value)}
                      className={errors.invoice_date ? 'error' : ''}
                      disabled={isFieldLocked}
                    />
                    <CopyButton value={formData.invoice_date ? formatDate(formData.invoice_date) : ''} title="Copy date" />
                  </div>
                  {errors.invoice_date && <div className="error-message">{errors.invoice_date}</div>}
                </div>

                <div className="form-group date-group">
                  <label htmlFor="edit_recorded_date" title="Date this invoice is recorded against for budgeting. Defaults to the invoice date.">Recorded</label>
                  <div className="input-with-copy">
                    {/* Editable even when the invoice is locked — recorded date is
                        a budgeting attribute, not part of the invoice document. */}
                    <input
                      id="edit_recorded_date"
                      type="date"
                      value={formData.recorded_date}
                      onChange={(e) => handleInputChange('recorded_date', e.target.value)}
                    />
                    <CopyButton value={formData.recorded_date ? formatDate(formData.recorded_date) : ''} title="Copy recorded date" />
                  </div>
                </div>
              </div>

              {/* Row 2: Project selector, Job #, Client, Status */}
              <div className="form-row form-row-project">
                <div className="form-group project-group">
                  <label htmlFor="edit_project_id">Job *</label>
                  <SearchableProjectDropdown
                    value={formData.project_id || null}
                    onChange={handleProjectChange}
                    placeholder="Search and select job..."
                    allowNone={false}
                    className={errors.project_id ? 'error' : ''}
                    disabled={isFieldLocked}
                  />
                  {errors.project_id && <div className="error-message">{errors.project_id}</div>}
                </div>

                <div className="form-group job-number-display-group">
                  <label>Job #</label>
                  <div className="display-with-copy">
                    <span className="job-number-display">
                      {invoice.job_number || '-'}
                    </span>
                    {invoice.job_number && (
                      <CopyButton value={String(invoice.job_number)} title="Copy job number" />
                    )}
                  </div>
                </div>

                <div className="form-group client-group">
                  <label>Client</label>
                  <div className="display-with-copy">
                    <div className={`client-display ${selectedClientName ? 'has-client' : 'no-client'}`}>
                      {selectedClientName || 'Select a job'}
                    </div>
                    {selectedClientName && (
                      <CopyButton value={selectedClientName} title="Copy client name" />
                    )}
                  </div>
                </div>

                <div className="form-group status-group">
                  <label htmlFor="edit_status">Status</label>
                  {isBundled ? (
                    <div className="status-locked" title="Status cascades from the parent invoice">
                      {formData.status} <span className="status-locked__hint">(follows parent)</span>
                    </div>
                  ) : (
                    <Dropdown
                      items={statusDropdownItems}
                      value={formData.status}
                      onChange={(value) => handleInputChange('status', value)}
                      placeholder="Select status"
                    />
                  )}
                </div>
              </div>

              {/* Calculation Summary with Fee Inputs */}
              <div className="calculation-summary calculation-summary--with-copy">
                <div className="calc-row input-row">
                  <label htmlFor="edit_our_fee" className="label">Our Fee *</label>
                  <div className="calc-value-with-copy">
                    <div className="currency-input">
                      <span className="currency-symbol">$</span>
                      <input
                        id="edit_our_fee"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.our_fee}
                        onChange={(e) => handleInputChange('our_fee', e.target.value)}
                        className={errors.our_fee ? 'error' : ''}
                        placeholder="0.00"
                        disabled={isFieldLocked || isFeeManagedByEditor}
                        title={isFeeManagedByEditor ? 'Managed by the tax invoice editor — change line items there' : undefined}
                      />
                    </div>
                    <CopyButton value={ourFee.toFixed(2)} title="Copy our fee" />
                  </div>
                </div>
                {errors.our_fee && <div className="error-message calc-error">{errors.our_fee}</div>}
                
                <div className="calc-row input-row">
                  <label htmlFor="edit_disbursements" className="label">Disbursements</label>
                  <div className="calc-value-with-copy">
                    <div className="currency-input">
                      <span className="currency-symbol">$</span>
                      <input
                        id="edit_disbursements"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.disbursements}
                        onChange={(e) => handleInputChange('disbursements', e.target.value)}
                        placeholder="0.00"
                        disabled={isFieldLocked || isFeeManagedByEditor}
                        title={isFeeManagedByEditor ? 'Managed by the tax invoice editor — change line items there' : undefined}
                      />
                    </div>
                    <CopyButton value={disbursements.toFixed(2)} title="Copy disbursements" />
                  </div>
                </div>
                
                <div className="calc-row subtotal">
                  <span className="label">Subtotal:</span>
                  <span className="value">{formatCurrency(ourFee + disbursements)}</span>
                </div>
                <div className="calc-row">
                  <span className="label">GST (10%):</span>
                  <span className="value">{formatCurrency(gstAmount)}</span>
                </div>
                <div className="calc-row total">
                  <span className="label">Total Amount:</span>
                  <div className="calc-value-with-copy">
                    <span className="value">{formatCurrency(totalAmount)}</span>
                    <CopyButton value={totalAmount.toFixed(2)} title="Copy total amount" />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="form-group" id="notes-section">
                <label htmlFor="edit_notes">Notes</label>
                <textarea
                  id="edit_notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Optional notes about this invoice..."
                  rows={3}
                  disabled={isFieldLocked}
                />
              </div>

              {/* Metadata */}
              <div className="metadata-section">
                <span className="metadata-item">Created: {formatDate(invoice.created_at)}</span>
                <span className="metadata-item">Last updated: {formatDate(invoice.updated_at)}</span>
              </div>

          </form>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && createPortal(
        <div 
          className="delete-confirm-overlay"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            // Close modal if clicking on overlay (not the modal content)
            if (e.target === e.currentTarget) {
              setShowDeleteConfirm(false);
            }
          }}
        >
          <div 
            className="delete-confirm-modal"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete this invoice? This action cannot be undone.
            </p>
            <p className="project-name">
              <strong>{invoice.invoice_number}</strong>
            </p>
            <div className="confirm-actions">
              <button 
                type="button" 
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowDeleteConfirm(false);
                }} 
                className="btn-secondary"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleDelete();
                }} 
                className="btn-danger"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Invoice'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default InvoiceDetailModal;
